import { credentialName } from "@/lib/credentials";
import { HadafBadge } from "@/masar/components/HadafBadge";
import { useState } from "react";
// نافذة «المتطلبات والتفاصيل» لشهادة/مؤهل من الدليل: معلومات → عروض سارية → «لدي هذه الشهادة» / «مهتم»
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { errText } from "@/v2/lib/hooks";
import { Chip } from "@/v2/components/ui";
import { Modal } from "@/v2/admin/ui";
import { KIND_AR, fmtDate } from "@/masar/lib";
import { Heart, HeartOff, Upload, ExternalLink, Tags, CheckCircle2, Eye, Star } from "lucide-react";

const GUIDE_KEYS = ["masar-cert-guide", "masar-journey", "masar-offers", "masar-certs"];
export const refreshGuide = (qc) => GUIDE_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

export const useInterest = () => {
  const qc = useQueryClient();
  return async (item) => {
    try {
      const { data: r } = await api.post(`/masar/interests/${item.id}/toggle`);
      toast.success(r.interested ? "سُجل اهتمامك — سنبلغك بأي عرض جديد" : "أُلغي الاهتمام");
      refreshGuide(qc);
      return r.interested;
    } catch (e) { toast.error(errText(e)); return item.interested; }
  };
};

export const Price = ({ o }) => (o.price != null ? <b className="text-ink" dir="ltr">{Number(o.price).toLocaleString("en-US")} {o.currency || "SAR"}</b> : <span className="text-ink-2">السعر غير منشور</span>);

const Row = ({ label, children }) => (children ? <div className="text-sm"><span className="text-ink-2">{label}: </span>{children}</div> : null);

export function CertDetails({ item, onClose, onHave }) {
  const navigate = useNavigate();
  const toggle = useInterest();
  const [interested, setInterested] = useState(!!item.interested);
  const isAcademic = item.kind === "academic";
  return (
    <Modal title={credentialName(item)} onClose={onClose} wide testId="guide-details">
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap gap-1.5">
          <HadafBadge support={item.hrdf} />
          <Chip tone="muted">{KIND_AR[item.kind] || KIND_AR.professional}</Chip>
          {item.my_status_ar && <Chip tone={item.my_status === "approved" ? "emerald" : "gold"}>{item.my_status === "approved" ? "لديك شهادة معتمدة" : item.my_status_ar}</Chip>}
        </div>

        <section className="v2-inset p-4 space-y-1.5" data-testid="guide-info">
          <div className="text-xs text-beige-ink font-bold mb-1">معلومات {isAcademic ? "المؤهل" : "الشهادة"}</div>
          {item.desc && <p className="text-ink-2 leading-relaxed">{item.desc}</p>}
          {item.hrdf?.status !== "not_applicable" && item.hrdf?.checked_at && <p className="text-xs text-ink-2">آخر تحقق من دعم هدف: {item.hrdf.checked_at} · {item.hrdf.note}</p>}
          {item.mapping_reason && <p className="text-xs text-ink-2">{item.mapping_reason}</p>}
          <Row label="الجهة المانحة">{item.issuer}</Row>
          <Row label="مجال الدراسة">{item.field}</Row>
          <Row label="المدة">{item.duration}</Row>
          <Row label={isAcademic ? "طريقة الدراسة" : "نوع الدراسة أو الاختبار"}>{item.study_mode}</Row>
          {item.prerequisites?.length > 0 && <div><span className="text-ink-2">{isAcademic ? "شروط القبول" : "متطلبات التقديم"}:</span><ul className="list-disc pr-5 text-ink-2 mt-0.5 space-y-0.5">{item.prerequisites.map((x) => <li key={x}>{x}</li>)}</ul></div>}
          {!isAcademic && <Row label="الصلاحية والتجديد">{item.validity_months ? `${item.validity_months} شهرًا ثم تجديد` : "بحسب متطلبات الجهة المانحة"}</Row>}
          {item.alternatives?.length > 0 && <div><span className="text-ink-2">بدائل معتمدة (يكفي أحدها): </span><span className="inline-flex flex-wrap gap-1">{item.alternatives.map((x) => <Chip key={x} tone="muted">{x}</Chip>)}</span></div>}
          {item.links?.length > 0 && <div className="flex flex-wrap gap-2 pt-1">{item.links.map((l) => <a key={l} href={l} target="_blank" rel="noreferrer" className="v2-btn-ghost v2-btn-sm"><ExternalLink size={12} /> الرابط الرسمي</a>)}</div>}
        </section>

        <section data-testid="guide-offers">
          <div className="text-xs text-beige-ink font-bold mb-2 flex items-center gap-1.5"><Tags size={13} /> العروض والأسعار المتاحة ({item.offers?.length || 0})</div>
          {item.offers?.length ? (
            <div className="grid sm:grid-cols-2 gap-2">
              {item.offers.map((o) => (
                <div key={o.id} className={`v2-inset p-3 text-xs space-y-1 ${o.featured ? "!border-beige/50" : ""}`} data-testid="guide-offer">
                  <div className="flex items-center justify-between gap-2"><b className="text-sm">{o.title}</b>{o.featured && <Chip tone="gold"><Star size={10} /> موصى به</Chip>}</div>
                  <div className="text-ink-2">{o.provider}{o.delivery ? ` · ${o.delivery}` : ""}{o.duration ? ` · ${o.duration}` : ""}</div>
                  <div><Price o={o} />{o.includes ? <span className="text-ink-2"> — يشمل: {o.includes}</span> : null}</div>
                  {o.valid_until && <div className="text-ink-2">ساري حتى {fmtDate(o.valid_until)}</div>}
                </div>
              ))}
            </div>
          ) : <div className="v2-inset p-3 text-xs text-ink-2" data-testid="guide-no-offers">لا يوجد عرض متاح حاليًا — سجّل اهتمامك ليصلك أي عرض جديد.</div>}
          {item.offers?.length > 0 && <button onClick={() => { onClose(); navigate("/masar/offers"); }} className="v2-btn-ghost v2-btn-sm mt-2" data-testid="guide-goto-offers">مقارنة العروض في «عروض التدريب»</button>}
        </section>

        <section className="flex flex-wrap gap-2 pt-3 border-t border-line" data-testid="guide-actions">
          {!item.my_cert_id && <button onClick={onHave} className="v2-btn v2-btn-sm" data-testid="guide-have"><Upload size={13} /> {isAcademic ? "لدي هذا المؤهل" : "لدي هذه الشهادة"}</button>}
          {item.my_cert_id && item.my_status === "approved" && <button onClick={() => { onClose(); navigate("/masar/certificates"); }} className="v2-btn v2-btn-sm" data-testid="guide-view-mine"><Eye size={13} /> عرض شهادتي</button>}
          {item.my_cert_id && item.my_status !== "approved" && <button onClick={() => { onClose(); navigate("/masar/certificates"); }} className="v2-btn-ghost v2-btn-sm" data-testid="guide-track-mine"><CheckCircle2 size={13} /> متابعة طلبي ({item.my_status_ar})</button>}
          {!isAcademic && item.my_status !== "approved" && (
            <button onClick={async () => setInterested(await toggle({ ...item, interested }))} className={`v2-btn-sm ${interested ? "v2-btn" : "v2-btn-ghost"}`} data-testid="guide-interest">
              {interested ? <HeartOff size={13} /> : <Heart size={13} />} {interested ? "إلغاء الاهتمام" : "مهتم بهذه الشهادة"}
            </button>
          )}
        </section>
      </div>
    </Modal>
  );
}
